import { languageKeys } from '../../util/data';
import { richTextToMetaDescription } from '../../util/seo';
import { LISTING_STATE_DRAFT, LISTING_STATE_PENDING_APPROVAL } from '../../util/types';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PARAM_TYPE_NEW,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  createSlug,
} from '../../util/urlHelpers';
import {
  buildPackagesSaveData,
  getListingFaq,
  getListingPackages,
} from '../../util/listingPackages';
import { readRelatedIds, relatedIdsForSave } from '../../util/relatedListings';

// The editor's listing states: 'new' | 'draft' | 'published'.
const EDITOR_STATE_NEW = 'new';
const EDITOR_STATE_DRAFT = 'draft';
const EDITOR_STATE_PUBLISHED = 'published';

// requestUpdateListing marks this tab updated; the single-page editor has one.
const SERVICE_TAB = 'service';

/**
 * The editor's listing state: 'new' on the new-listing URL, 'draft' for a
 * draft, and 'published' for anything else (pending approval and closed
 * listings stay as they are when saved).
 */
export const editorListingState = (paramsType, listing) => {
  if (paramsType === LISTING_PAGE_PARAM_TYPE_NEW) {
    return EDITOR_STATE_NEW;
  }
  const state = listing && listing.attributes && listing.attributes.state;
  return state === LISTING_STATE_DRAFT ? EDITOR_STATE_DRAFT : EDITOR_STATE_PUBLISHED;
};

// Uploads are { id: tempId, file } until the upload resolves with imageId;
// saved listing images have no file.
export const isImageUploading = image => !!image.file && !image.imageId;

/**
 * Image ids for the SDK, in display order: saved images by id, finished
 * uploads by imageId. Running uploads have no id yet and are skipped.
 */
export const imageIdsForSave = images =>
  images.filter(image => !isImageUploading(image)).map(image => image.imageId || image.id);

/**
 * EditServiceListingForm values for a listing (or blank values for a new
 * one). Packages and the FAQ come from the read adapter, so old listings open
 * with their legacy packages.
 */
export const initialValuesFromListing = listing => {
  const attributes = (listing && listing.attributes) || {};
  const publicData = attributes.publicData || {};
  return {
    title: attributes.title || '',
    images: (listing && listing.images) || [],
    description: publicData.descriptionText ?? attributes.description ?? '',
    packages: getListingPackages(listing),
    faq: getListingFaq(listing),
    packageDraft: null,
    faqDraft: null,
    relatedWorkIds: readRelatedIds(publicData.relatedWorkIds),
    relatedServiceIds: readRelatedIds(publicData.relatedServiceIds),
  };
};

/**
 * SDK params for creating or updating a service listing from the form values.
 * Writes packages, faq and price (from package 1); the legacy package keys,
 * priceArray, rules, category and categoryNodes are never sent, so they stay
 * as they are. The related
 * picks are checked against `relatedPools` ({ works, services }, the loaded
 * pickable listings); without it (the list didn't load) they are sent as saved.
 */
export const buildServiceListingParams = (
  values,
  { currentUser, imageIds, listingId, relatedPools = null }
) => {
  const { title, description, packages = [], faq = [] } = values;
  // buildPackagesSaveData needs at least one package; a draft may have none.
  const priceMaybe = packages.length > 0 ? { price: buildPackagesSaveData(packages).price } : {};
  const profileLanguages = currentUser?.attributes?.profile?.publicData?.languages;
  // An empty list would wipe the listing's own languages and drop it from the
  // language filter on service search.
  const languages = languageKeys(profileLanguages);
  const languagesMaybe = languages.length > 0 ? { languages } : {};

  return {
    ...(listingId ? { id: listingId } : {}),
    title: (title || '').trim(),
    description: richTextToMetaDescription(description),
    images: imageIds,
    ...priceMaybe,
    publicData: {
      descriptionText: description,
      packages,
      faq,
      ...languagesMaybe,
      relatedWorkIds: relatedIdsForSave(
        values.relatedWorkIds,
        relatedPools ? relatedPools.works : null
      ),
      relatedServiceIds: relatedIdsForSave(
        values.relatedServiceIds,
        relatedPools ? relatedPools.services : null
      ),
    },
  };
};

/**
 * Where the editor goes after a save: new and draft listings to the draft
 * variant of the listing page (it offers Publish), listings pending approval
 * to their own variant (the public listing page can't load them), and
 * published ones to the listing page.
 */
export const saveTargetRoute = (listing, listingState) => {
  const params = { id: listing.id.uuid, slug: createSlug(listing.attributes.title) };
  const variantRoute = variant => ({ name: 'ListingPageVariant', params: { ...params, variant } });
  if (listingState !== EDITOR_STATE_PUBLISHED) {
    return variantRoute(LISTING_PAGE_DRAFT_VARIANT);
  }
  return listing.attributes.state === LISTING_STATE_PENDING_APPROVAL
    ? variantRoute(LISTING_PAGE_PENDING_APPROVAL_VARIANT)
    : { name: 'ListingPage', params };
};

// The thunks resolve with an error action instead of rejecting
const listingOrThrow = result => {
  if (result && result.error === true) {
    throw result.payload;
  }
  return result.data.data;
};

/**
 * Saves the form values: a new listing becomes a draft, and draft and
 * published listings are updated in place (a published one stays published).
 * Resolves with the saved listing; rejects with the stored error.
 */
export const submitServiceListing = ({
  values,
  listing,
  listingState,
  currentUser,
  imageIds,
  relatedPools = null,
  onCreateListingDraft,
  onUpdateListing,
}) => {
  if (listingState === EDITOR_STATE_NEW) {
    const params = buildServiceListingParams(values, { currentUser, imageIds, relatedPools });
    return onCreateListingDraft(params).then(listingOrThrow);
  }
  const params = buildServiceListingParams(values, {
    currentUser,
    imageIds,
    listingId: listing.id,
    relatedPools,
  });
  return onUpdateListing(SERVICE_TAB, params, currentUser).then(listingOrThrow);
};
